import { SearchOutlined } from '@ant-design/icons'
import { PageHeader } from '@ant-design/pro-layout'
import Editor from '@monaco-editor/react'
import { Alert, Button, Col, Form, Row, Space, Typography } from 'antd'
import { useForm } from 'antd/es/form/Form'
import { t } from 'i18next'
import { Trans } from 'react-i18next'

import { hasCustomSearchQuery } from '../../../domain/viewConfig/searchQuery'
import { useViewConfigMutation, useViewConfigSheet } from '../../../recoil/viewConfig'
import { formatSqlWithMustache } from './formatSqlWithMustache'

interface FormValue {
  sql: string
}

export const CustomSearchQueryForm = ({
  onAfterFinish,
  setHasChangeToViewSchema,
}: {
  onAfterFinish: () => void
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}) => {
  const [form] = useForm<FormValue>()
  const config = useViewConfigSheet()
  const { setMeta, clearCustomSearchQuery } = useViewConfigMutation()
  const hasCustomQuery = hasCustomSearchQuery(config.meta?.searchQuery)

  const onFinish = (value: FormValue) => {
    setMeta({
      config,
      newMeta: {
        searchQuery: value.sql === '' ? undefined : value.sql,
      },
    })
    setHasChangeToViewSchema(true)
    onAfterFinish()
  }

  // 将来的にちゃんとしたGUIを用意するが、いったんSQLを直で書いてもらう
  return (
    <PageHeader
      title={
        <Space>
          <SearchOutlined />
          <span>{t(`検索条件設定`)}</span>
        </Space>
      }
    >
      <Alert
        className="mb-4"
        message={<div>{t(`検索条件SQLは構文チェックされません。誤りがあると検索がエラーになります。`)}</div>}
        type="warning"
        showIcon
      />
      <div className="mb-8">
        {t(`設定方法`)}
        <br />・
        {t(
          `SQLのWHERE句に相当するクエリを記述してください。この条件設定を利用した場合、通常の検索項目設定は無視されます`,
        )}
        <br />
        <Trans>
          <div>
            ・mustache記法で<Typography.Text code>query</Typography.Text>
            という値を指定することで、検索クエリを埋め込めます
          </div>
          <div>
            （例）
            <Typography.Text code>{"phone ='{{ query }}'"}</Typography.Text>
            と書いた上で、検索フィールドに<Typography.Text code>08012345678</Typography.Text>と入力すると、
            <Typography.Text code>phone = &#39;08012345678&#39;</Typography.Text>という条件でSQLが発行されます。
          </div>
        </Trans>
      </div>

      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        initialValues={{
          sql: formatSqlWithMustache(config.meta?.searchQuery ?? ''),
        }}
      >
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item
              name="sql"
              label={
                <div>
                  {t(`検索条件`)}(SQL){' '}
                  <Button
                    size="small"
                    onClick={() => {
                      const { sql } = form.getFieldsValue()
                      form.setFieldsValue({ sql: formatSqlWithMustache(sql) })
                    }}
                  >
                    {t(`フォーマット`)}
                  </Button>
                </div>
              }
              rules={[{ required: true, message: t(`検索条件を入力してください`) }]}
            >
              <Editor
                height={350}
                // width={500}
                theme="vs-dark"
                defaultLanguage="sql"
                options={{
                  minimap: {
                    enabled: false,
                  },
                }}
                defaultValue={`---(例)\nsalesforce_contact.phone = regexp_replace('{{ query }}', '-', '', 'g')`}
              />
            </Form.Item>
          </Col>
        </Row>
        <div className="flex flex-row-reverse">
          <Form.Item>
            <Space>
              {hasCustomQuery ? (
                <Button
                  onClick={() => {
                    clearCustomSearchQuery()
                    setHasChangeToViewSchema(true)
                  }}
                >
                  {t(`リセット`)}
                </Button>
              ) : null}
              <Button htmlType="submit">{t(`適用`)}</Button>
            </Space>
          </Form.Item>
        </div>
      </Form>
    </PageHeader>
  )
}
